import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Search, Plus, Trash2, Eye, Share2, ArrowRight, Play, CheckCircle, ListChecks, MessageSquare, Send } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeTransferPage = () => {
  const { token, user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', from_user_id: '', to_user_id: '', priority: 'medium',
    knowledge_areas: [],
  });
  const [checklistInput, setChecklistInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [knowledgeAreaInput, setKnowledgeAreaInput] = useState('');

  useEffect(() => {
    fetchTransfers();
    fetchUsers();
  }, [search, statusFilter]);

  const fetchTransfers = async () => {
    try {
      const params = { search };
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${API}/knowledge-transfer`, {
        params, headers: { Authorization: `Bearer ${token}` }
      });
      setTransfers(response.data);
    } catch {
      toast.error('Failed to fetch transfers');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch { /* silent */ }
  };

  const handleCreate = async () => {
    if (!form.title || !form.from_user_id || !form.to_user_id) {
      toast.error('Title, mentor, and mentee are required');
      return;
    }
    try {
      await axios.post(`${API}/knowledge-transfer`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Knowledge transfer created');
      setIsCreateOpen(false);
      setForm({ title: '', description: '', from_user_id: '', to_user_id: '', priority: 'medium', knowledge_areas: [] });
      fetchTransfers();
    } catch {
      toast.error('Failed to create transfer');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this knowledge transfer?')) return;
    try {
      await axios.delete(`${API}/knowledge-transfer/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transfer deleted');
      fetchTransfers();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleStatusUpdate = async (ktId, status) => {
    try {
      await axios.put(`${API}/knowledge-transfer/${ktId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      refreshSelected(ktId);
      fetchTransfers();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSetChecklist = async (ktId) => {
    if (!checklistInput.trim()) return;
    const items = checklistInput.split('\n').filter(l => l.trim());
    try {
      await axios.put(`${API}/knowledge-transfer/${ktId}/checklist`, { items }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Checklist set');
      setChecklistInput('');
      refreshSelected(ktId);
    } catch {
      toast.error('Failed to set checklist');
    }
  };

  const handleToggleChecklistItem = async (ktId, index, completed) => {
    try {
      const res = await axios.put(`${API}/knowledge-transfer/${ktId}/checklist/toggle`, { item_index: index, completed }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTransfer(prev => ({
        ...prev,
        checklist: res.data.checklist,
        progress: res.data.progress
      }));
      fetchTransfers();
    } catch {
      toast.error('Failed to update item');
    }
  };

  const handleAddNote = async (ktId) => {
    if (!noteInput.trim()) return;
    try {
      await axios.post(`${API}/knowledge-transfer/${ktId}/notes`, { content: noteInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note added');
      setNoteInput('');
      refreshSelected(ktId);
    } catch {
      toast.error('Failed to add note');
    }
  };

  const refreshSelected = async (ktId) => {
    try {
      const res = await axios.get(`${API}/knowledge-transfer/${ktId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTransfer(res.data);
    } catch { /* silent */ }
  };

  const openView = async (transfer) => {
    try {
      const res = await axios.get(`${API}/knowledge-transfer/${transfer.kt_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTransfer(res.data);
      setIsViewOpen(true);
    } catch {
      setSelectedTransfer(transfer);
      setIsViewOpen(true);
    }
  };

  const addKnowledgeArea = () => {
    if (knowledgeAreaInput.trim() && !form.knowledge_areas.includes(knowledgeAreaInput.trim())) {
      setForm({ ...form, knowledge_areas: [...form.knowledge_areas, knowledgeAreaInput.trim()] });
      setKnowledgeAreaInput('');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return 'bg-[#34C759] text-white';
      case 'in_progress': return 'bg-[#007AFF] text-white';
      default: return 'bg-[#FFCC00] text-[#0A0A0A]';
    }
  };

  const getPriorityBadge = (p) => {
    if (p === 'high') return 'bg-[#FF3B30] text-white';
    if (p === 'low') return 'bg-[#E5E7EB] text-[#4B5563]';
    return 'bg-[#FF9500] text-white';
  };

  return (
    <div data-testid="knowledge-transfer-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Knowledge Transfer</h1>
          <p className="text-[#4B5563] text-sm">Structured knowledge transfer between team members</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsCreateOpen(true)} data-testid="create-transfer-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Transfer
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-[#E5E7EB] mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input placeholder="Search transfers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="transfer-search" />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transfers Table */}
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
          ) : transfers.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF]">
              <Share2 className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
              <p>No knowledge transfers yet.</p>
            </div>
          ) : (
            <Table data-testid="transfers-table">
              <TableHeader>
                <TableRow className="bg-[#F3F4F6]">
                  <TableHead>Title</TableHead>
                  <TableHead>Mentor → Mentee</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map((t) => (
                  <TableRow key={t.kt_id} data-testid={`transfer-row-${t.kt_id}`}>
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span>{t.from_user_name || 'Unknown'}</span>
                        <ArrowRight className="w-3 h-3 text-[#9CA3AF]" />
                        <span>{t.to_user_name || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge className={getPriorityBadge(t.priority)}>{t.priority}</Badge></TableCell>
                    <TableCell><Badge className={getStatusBadge(t.status)}>{t.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                          <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${t.progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-[#9CA3AF]">{t.progress || 0}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#9CA3AF] text-sm">{format(new Date(t.created_at), 'MMM d, yyyy')}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openView(t)} data-testid={`view-transfer-${t.kt_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDelete(t.kt_id)} data-testid={`delete-transfer-${t.kt_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-transfer-dialog">
          <DialogHeader><DialogTitle>Create Knowledge Transfer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., API Design Best Practices Handover" data-testid="transfer-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the knowledge being transferred..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mentor (From) *</Label>
                <Select value={form.from_user_id} onValueChange={(v) => setForm({ ...form, from_user_id: v })}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Select mentor" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mentee (To) *</Label>
                <Select value={form.to_user_id} onValueChange={(v) => setForm({ ...form, to_user_id: v })}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Select mentee" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Knowledge Areas</Label>
              <div className="flex gap-2">
                <Input value={knowledgeAreaInput} onChange={(e) => setKnowledgeAreaInput(e.target.value)} placeholder="Add area..." onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKnowledgeArea())} />
                <Button variant="outline" size="sm" onClick={addKnowledgeArea}>Add</Button>
              </div>
              {form.knowledge_areas.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {form.knowledge_areas.map((a, i) => (
                    <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setForm({ ...form, knowledge_areas: form.knowledge_areas.filter((_, j) => j !== i)})}>
                      {a} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} data-testid="save-transfer-btn">Create Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Detail Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-transfer-dialog">
          <DialogHeader><DialogTitle>Transfer Details</DialogTitle></DialogHeader>
          {selectedTransfer && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{selectedTransfer.title}</h3>
                  {selectedTransfer.description && <p className="text-sm text-[#4B5563] mt-1">{selectedTransfer.description}</p>}
                </div>
                <div className="flex gap-2">
                  <Badge className={getPriorityBadge(selectedTransfer.priority)}>{selectedTransfer.priority}</Badge>
                  <Badge className={getStatusBadge(selectedTransfer.status)}>{selectedTransfer.status}</Badge>
                </div>
              </div>

              {/* Mentor → Mentee */}
              <div className="p-4 bg-[#F3F4F6] rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-xs text-[#9CA3AF]">Mentor</p>
                    <p className="font-medium text-sm">{selectedTransfer.from_user_name}</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-[#002FA7]" />
                  <div className="text-center">
                    <p className="text-xs text-[#9CA3AF]">Mentee</p>
                    <p className="font-medium text-sm">{selectedTransfer.to_user_name}</p>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Progress</span>
                  <span className="text-sm font-bold text-[#002FA7]">{selectedTransfer.progress || 0}%</span>
                </div>
                <div className="w-full h-3 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${selectedTransfer.progress || 0}%` }} />
                </div>
              </div>

              {/* Status Actions */}
              {selectedTransfer.status !== 'completed' && (
                <div className="flex gap-2">
                  {selectedTransfer.status === 'pending' && (
                    <Button size="sm" className="bg-[#007AFF] hover:bg-[#0056B3]" onClick={() => handleStatusUpdate(selectedTransfer.kt_id, 'in_progress')} data-testid="start-transfer-btn">
                      <Play className="w-3.5 h-3.5 mr-1.5" />Start Transfer
                    </Button>
                  )}
                  {selectedTransfer.status === 'in_progress' && (
                    <Button size="sm" className="bg-[#34C759] hover:bg-[#2BAF4A]" onClick={() => handleStatusUpdate(selectedTransfer.kt_id, 'completed')} data-testid="complete-transfer-btn">
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Mark Complete
                    </Button>
                  )}
                </div>
              )}

              {/* Knowledge Areas */}
              {selectedTransfer.knowledge_areas?.length > 0 && (
                <div>
                  <Label className="text-xs text-[#9CA3AF] uppercase tracking-wider">Knowledge Areas</Label>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {selectedTransfer.knowledge_areas.map((a, i) => <Badge key={i} variant="secondary">{a}</Badge>)}
                  </div>
                </div>
              )}

              {/* Checklist */}
              <div className="border-t border-[#E5E7EB] pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <ListChecks className="w-4 h-4 text-[#002FA7]" />
                  <span className="text-sm font-medium">Checklist ({selectedTransfer.checklist?.length || 0} items)</span>
                </div>
                {selectedTransfer.checklist?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTransfer.checklist.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-[#F9FAFB]" data-testid={`checklist-item-${idx}`}>
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={(v) => handleToggleChecklistItem(selectedTransfer.kt_id, idx, v)}
                          disabled={selectedTransfer.status === 'completed'}
                        />
                        <span className={`text-sm ${item.completed ? 'line-through text-[#9CA3AF]' : ''}`}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Textarea
                      value={checklistInput}
                      onChange={(e) => setChecklistInput(e.target.value)}
                      placeholder="Add checklist items (one per line):&#10;Review API documentation&#10;Shadow debugging session&#10;Complete handover document"
                      rows={4}
                      data-testid="checklist-input"
                    />
                    <Button size="sm" variant="outline" onClick={() => handleSetChecklist(selectedTransfer.kt_id)} data-testid="set-checklist-btn">
                      <ListChecks className="w-3.5 h-3.5 mr-1.5" />Set Checklist
                    </Button>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="border-t border-[#E5E7EB] pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-[#002FA7]" />
                  <span className="text-sm font-medium">Notes ({selectedTransfer.notes?.length || 0})</span>
                </div>
                {selectedTransfer.notes?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {selectedTransfer.notes.map((note) => (
                      <div key={note.note_id} className="p-3 bg-[#F3F4F6] rounded text-sm">
                        <p>{note.content}</p>
                        <p className="text-xs text-[#9CA3AF] mt-1">{note.user_name} - {format(new Date(note.created_at), 'MMM d, h:mm a')}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Add a note..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote(selectedTransfer.kt_id)}
                    data-testid="note-input"
                  />
                  <Button size="sm" variant="outline" onClick={() => handleAddNote(selectedTransfer.kt_id)} data-testid="add-note-btn">
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeTransferPage;
